import { create } from 'zustand';

import { useScriptStore } from '../../scripting/scriptStore';

// The one bridge from the Code workspace's other panels into the live Monaco
// instance. Output, Problems, the Outline and the Reference panel are all
// separate dockview panels with no React parent in common with the editor,
// so "jump to line 12 of boot.hws" or "insert `entities.spawn(...)` at the
// cursor" cannot be a prop - the mounted HollowScriptFileEditor registers
// what it can do here, and everyone else calls it. A jump into a file that
// is not the one on screen first opens that file (via the script store) and
// parks the position as `pendingReveal` for the editor to pick up the
// moment it has mounted that file's model.

export interface EditorCommands {
  /** The script whose model the registered editor currently shows. */
  readonly fileId: string;
  revealPosition(line: number, column: number): void;
  insertText(text: string): void;
  /** Runs one of Monaco's own actions by id (`actions.find`,
   * `editor.action.formatDocument`, `editor.action.quickOutline`, ...). */
  trigger(actionId: string): void;
  focus(): void;
}

export interface PendingReveal {
  readonly scriptId: string;
  readonly line: number;
  readonly column: number;
}

interface EditorCommandsState {
  readonly commands: EditorCommands | null;
  readonly pendingReveal: PendingReveal | null;
  /** The live caret, for the status bar. */
  readonly cursor: { readonly line: number; readonly column: number } | null;
  register: (commands: EditorCommands) => () => void;
  setCursor: (line: number, column: number) => void;
  /** Hands back (and clears) a parked reveal for `fileId`, if any. */
  consumePendingReveal: (fileId: string) => PendingReveal | null;
  setPendingReveal: (reveal: PendingReveal | null) => void;
}

export const useEditorCommands = create<EditorCommandsState>((set, get) => ({
  commands: null,
  pendingReveal: null,
  cursor: null,

  register: (commands) => {
    set({ commands });
    return () => {
      if (get().commands === commands) set({ commands: null, cursor: null });
    };
  },

  setCursor: (line, column) => {
    const current = get().cursor;
    if (current && current.line === line && current.column === column) return;
    set({ cursor: { line, column } });
  },

  consumePendingReveal: (fileId) => {
    const pending = get().pendingReveal;
    if (!pending || pending.scriptId !== fileId) return null;
    set({ pendingReveal: null });
    return pending;
  },

  setPendingReveal: (reveal) => set({ pendingReveal: reveal }),
}));

/** Opens `scriptId` in the editor and moves the caret to `line:column` -
 * immediately if that file is already on screen, otherwise as soon as it
 * is. Every "click a problem / output line / outline entry" affordance in
 * the Code workspace ends up here. */
export function revealInScript(scriptId: string, line: number, column = 1): void {
  useScriptStore.getState().openScript(scriptId);
  const state = useEditorCommands.getState();
  if (state.commands && state.commands.fileId === scriptId) {
    state.commands.revealPosition(line, column);
    state.commands.focus();
    return;
  }
  state.setPendingReveal({ scriptId, line, column });
}

/** Inserts `text` at the caret of whichever file is open, if one is. */
export function insertIntoActiveScript(text: string): boolean {
  const commands = useEditorCommands.getState().commands;
  if (!commands) return false;
  commands.insertText(text);
  commands.focus();
  return true;
}

/** Runs a Monaco action against the open editor, if one is mounted. */
export function triggerEditorAction(actionId: string): boolean {
  const commands = useEditorCommands.getState().commands;
  if (!commands) return false;
  commands.trigger(actionId);
  return true;
}
